import { LoadingState } from "~/components/screens/StateScreen"

import { CheckIcon, ChevronRight } from "lucide-react"
import { useNavigate, useParams } from "react-router-dom"
import { ButtonPanel } from "~/components/ButtonPanel"
import { LoginPanel } from "~/components/LoginPanel"
import { FullScreen } from "~/components/screens/FullScreen"
import { authClient } from "~/lib/auth-client"
import { api } from "~/trpc/react"

export default function Page() {
	const { data: session, isPending: isSessionPending } = authClient.useSession()
	const params = useParams<{
		inviteId?: string
		orgSlug?: string
		id?: string
	}>()
	const navigate = useNavigate()

	const inviteId = params.inviteId ?? params.id ?? params.orgSlug ?? ""
	const {
		data: inviteData,
		isPending: isInvitePending,
		error: inviteError
	} = api.org.getInviteById.useQuery(
		{
			inviteId
		},
		{
			enabled: !!inviteId,
			retry: false,
			refetchOnWindowFocus: false
		}
	)

	const invite = inviteData?.invitation
	const inviteOrganization = invite?.organization
	const inviteTeam = invite && "team" in invite ? invite.team : null
	const inviteInviter = invite?.inviter

	const getOrgs = api.user.getOrgs.useQuery(undefined, {
		enabled: false
	})
	const getTeams = api.user.getTeams.useQuery(
		{
			organizationId: inviteOrganization?.id ?? ""
		},
		{
			enabled: !!inviteOrganization?.id
		}
	)

	const acceptInvite = api.org.acceptInvite.useMutation({
		onSuccess: (result) => {
			if (result.status === "accepted") {
				getOrgs.refetch()
				getTeams.refetch()
				navigate(
					`/${result.destination.orgSlug}${
						result.destination.teamSlug ? `/${result.destination.teamSlug}` : ""
					}`
				)
			}
		}
	})
	const acceptInviteError = acceptInvite.error
	const showInvalidInvite = !!inviteError || !invite

	const message = !invite ? null : (
		<div className="text-text-main font-normal">
			{inviteInviter ? (
				<>
					<span className="font-bold">{inviteInviter.orgMember.user.name}</span>{" "}
					invited you
				</>
			) : (
				"You have been invited"
			)}{" "}
			to join{" "}
			{inviteTeam ? (
				<>
					<span className="font-bold text-text-main">{inviteTeam.name}</span>{" "}
					in{" "}
				</>
			) : null}
			<span className="font-bold text-text-main">
				{inviteOrganization?.name}
			</span>{" "}
			on <span className="text-orbit-text">Orbit</span>
		</div>
	)

	const action = acceptInvite.data
	const alreadyMemberMessage =
		action?.status === "already_team_member"
			? `You're already in ${action.team.name}.`
			: action?.status === "already_org_member"
				? `You're already in ${action.organization.name}.`
				: null

	const actionButtons =
		action?.status === "already_team_member" ? (
			<ButtonPanel
				buttons={[
					{
						onClick: () =>
							navigate(`/${action.organization.slug}/${action.team.slug}`),
						icon: <ChevronRight />,
						children: "Go to Team"
					}
				]}
			/>
		) : action?.status === "already_org_member" ? (
			<ButtonPanel
				buttons={[
					{
						onClick: () => navigate(`/${action.organization.slug}`),
						icon: <ChevronRight />,
						children: "Go to Organization"
					}
				]}
			/>
		) : null

	if (!inviteId) {
		return (
			<FullScreen
				title="Invitation Not Found"
				message="This invite link is invalid or has expired."
			/>
		)
	}

	if (isInvitePending || isSessionPending) {
		return <LoadingState scope="page" branded />
	}

	if (showInvalidInvite || acceptInviteError) {
		return (
			<FullScreen
				title="Invitation Not Found"
				message="This invite link is invalid or has expired."
			/>
		)
	}

	return (
		<FullScreen
			title={`Join ${inviteOrganization?.name ?? "your team"}`}
			message={alreadyMemberMessage ?? message}
			bottomContent={
				!session ? (
					<LoginPanel
						callbackURL={`/invite/${inviteId}`}
						organizationSlug={inviteOrganization?.slug}
					/>
				) : action?.status === "already_team_member" ||
				  action?.status === "already_org_member" ? (
					actionButtons
				) : (
					<ButtonPanel
						buttons={[
							{
								onClick: () =>
									acceptInvite.mutate({
										inviteId
									}),
								icon: <CheckIcon />,
								children: "Accept Invite"
							}
						]}
					/>
				)
			}
		/>
	)
}
